"use client";

import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";

import HeroSection from "@/components/common/HeroSection";
import MiniCard from "@/components/common/MiniCard";
import Process from "@/components/common/Process";
import SectionHeader from "@/components/common/SectionHeader";
import BusinessCard from "@/components/common/BusinesCard";
import GradientBottom from "@/components/common/GradientBottom";

import {
  CardData,
  ProcessData,
  BusinessData1,
  BusinessData2,
} from "@/constants/SoftwareDev";

export default function SoftwareDev() {
  const isdark = useIsDark();

  return (
    <>
      <div className="relative flex flex-col justify-center items-center mt-6 mx-5 md:mx-[40px] xl:mx-[100px] 3xl:mx-[150] z-10">
        <section>
          <HeroSection
            heading="Software Development"
            headingStyles="text-[35px] mobile:text-[45px] sm:text-[60px] md:text-[75px] tablet:text-[95px] lg:text-[105px] xl:text-[135px] laptop:text-[155px] 3xl:text-[190px] 4xl:text-[210px] leading-[100%] text-center"
            subHeading="Turning bold ideas into powerful digital solutions."
            paragraph="We don’t just write code, we build solutions that work, scale, and make lives easier. Whether you’re starting from scratch or upgrading what you’ve already got, our team is here to turn your ideas into smart, reliable software that fits your business perfectly."
            paragraphStyles="max-w-[1228px]"
          />
        </section>

        <section className="flex flex-col w-full max-w-[2000px] items-center xl:mt-[133px] laptop:mt-0">
          <SectionHeader
            title={"What We Build"}
            wrapperClassName="xl:mt-0 xl:mb-0 px-0"
          />
          <div className="grid mobile:grid-cols-2 tablet:grid-cols-4 gap-[19px] md:gap-x-[35px] tablet:gap-4 xl:gap-[19px] 3xl:gap-[25px] xl:mt-[50px] auto-rows-fr">
            {CardData.map((item, index) => (
              <MiniCard
                key={index}
                icon={item.icon}
                heading={item.heading}
                paragraph={item.paragraph}
                cardStyles="md:pb-[35px] tablet:pb-[25px] tablet:px-3 lg:px-5 3xl:px-6 3xl:pb-10"
                headingStyles="tablet:text-[18px] lg:text-[20px] laptop:text-[22px]"
                paragraphStyles="laptop:text-[20px]"
                // cardStyles="w-[296px] h-[464px]"
              />
            ))}
          </div>
        </section>

        <section className="xl:mt-25">
          <SectionHeader
            title={"How We Work"}
            description={
              "We believe great software is built with heart, strategy, and a clear process."
            }
            wrapperClassName="xl:mt-0 xl:mb-0 px-0"
          />
        </section>

        <section className="flex justify-center w-full">
          <Process
            Data={ProcessData}
            sectionHeight={947}
            sectionStyles={twMerge(
              "xl:h-[947px]",
              isdark ? "bg-[#0F0F0F]" : "bg-[#F7F2FF]"
            )}
            isThereTwoMediumCubes={true}
            enableHoverEffect={true}
            smallCubeStyles="absolute xl:top-[167px] xl:left-[62px]"
            mediumCubeStyles="xl:top-[247px] xl:left-[258px]"
            secondmediumCubeStyles="absolute top-[390px] xl:top-[339px] xl:left-[52px]"
            largeCubeStyles="absolute xl:top-[431px] xl:left-[74px]"
          />
        </section>

        <section className="w-full max-w-[2000px] flex flex-col justify-center items-center">
          <SectionHeader
            title={"Technologies We Love"}
            wrapperClassName="xl:mt-25 mb-[50px] px-0"
          />

          <div className="flex w-full justify-between">
            {BusinessData1.map((item, index) => (
              <BusinessCard
                key={index}
                icon={item.icon}
                title={item.title}
                className="h-[45px] small:h-[50px] sm:h-[55px] md:h-[60px] lg:h-[65px] xl:h-[70px] 2xl:h-[75px] 3xl:h-[85px] px-3 small:px-[6px] mobile:px-2 mobile2:px-4 sm:px-3 md:px-[14px] xl:px-[18px] 2xl:px-6 3xl:px-7 rounded-[15px]"
                imageStyles="h-7 w-7 small:h-5 small:w-5 2xl:h-[42px] 2xl:w-[42px] 3xl:h-12 3xl:w-12"
                textStyles="text-[10px] mobile2:text-[12px] 3xl:text-[22px]"
                contentStyles="gap-1 flex flex-col small:flex-row items-center justify-center"
                hideText={true}
              />
            ))}
          </div>
          <div className="flex w-full gap-3 small:gap-2 mobile2:gap-5 sm:gap-2 md:gap-6 tablet:gap-14 lg:gap-18 xl:gap-[115px] mt-[30px] mx-auto justify-center">
            {BusinessData2.map((item, index) => (
              <BusinessCard
                key={index}
                icon={item.icon}
                title={item.title}
                className="h-[45px] small:h-[50px] sm:h-[55px] md:h-[60px] lg:h-[65px] xl:h-[70px] 2xl:h-[75px] 3xl:h-[85px] px-3 small:px-[6px] mobile:px-2 mobile2:px-4 sm:px-3 md:px-[14px] xl:px-[18px] 2xl:px-6 3xl:px-7"
                imageStyles="h-7 w-7 small:h-5 small:w-5 2xl:h-[42px] 2xl:w-[42px] 3xl:h-12 3xl:w-12"
                textStyles="text-[10px] mobile2:text-[12px] 3xl:text-[22px]"
                contentStyles="gap-1 flex flex-col small:flex-row items-center justify-center"
                hideText={true}
              />
            ))}
          </div>
        </section>
      </div>

      <section className="w-full mt-25">
        <GradientBottom
          heading="Let’s Build Something Great"
          subHeading="Whether it’s a big vision or a small idea, we’re here to make it real. You bring the dream - we’ll bring the tech, the team, and the coffee."
          text="Let’s create something amazing together."
          buttonText="Get In Touch"
          buttonHref="/contact"
        />
      </section>
    </>
  );
}
